import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import LearnMegaMenu from './LearnMegaMenu';
import { useAuth } from '../../contexts/AuthContext';
// Pilot disabled: restore when signup/purchase CTA returns.
// import { useAuthModal } from '../../contexts/AuthModalContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { listMyInstitutionAssignments } from '../../utils/api';
import { useDiscountCampaign } from '../DiscountBanner';

const ToolsDropdown = ({ isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="relative h-full flex items-center"
      ref={dropdownRef}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* <button
        className={`flex items-center gap-1.5 px-3 h-full text-xs font-medium transition-all border-b-2 ${
          isActive
            ? 'border-emerald-400 dark:border-primary text-slate-800 dark:text-primary'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
        }`}
      >
        Tools <Icon name="ChevronDown" size={10} className={isOpen ? 'rotate-180 transition-transform text-emerald-500 dark:text-primary' : 'transition-transform text-slate-400 dark:text-slate-500'} />
      </button> */}

      {isOpen && (
        <div className="absolute top-[100%] left-0 w-48 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl shadow-lg dark:shadow-none py-1.5 animate-slide-up z-50">
          <Link
            to="/resume-analyzer"
            className="flex items-center px-4 py-2 text-xs text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-primary/10 hover:text-emerald-700 dark:hover:text-primary transition-all font-medium"
            onClick={() => setIsOpen(false)}
          >
            Resume Analyzer
          </Link>
          <Link
            to="/sandbox"
            className="flex items-center px-4 py-2 text-xs text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-primary/10 hover:text-emerald-700 dark:hover:text-primary transition-all font-medium"
            onClick={() => setIsOpen(false)}
          >
            Code Sandbox
          </Link>
        </div>
      )}
    </div>
  );
};

const FreeBadge = () => (
  <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-black leading-none tracking-wider text-emerald-700 dark:bg-primary/15 dark:text-primary">
    free
  </span>
);

// Small count pill for pending institution assignments on the "Assigned" nav item.
const CountBadge = ({ count }) => (
  <span className="ml-1.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white dark:bg-primary dark:text-primary-foreground text-[9px] font-black leading-none">
    {count}
  </span>
);

const NavigationHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const { user, logout } = useAuth();
  // Pilot disabled: used by the hidden Start Free Trial CTA.
  // const { openAuthModal } = useAuthModal();
  const { isEnabled } = useFeatureFlags();
  const discountCampaign = useDiscountCampaign();
  const [isScrolled, setIsScrolled] = useState(false);
  // Institution B2B: surface an "Assigned" nav entry only for students who
  // actually have institution-assigned interviews. Non-institution users get an
  // empty list, so the link never appears for them (no clutter).
  const [assignmentInfo, setAssignmentInfo] = useState({ total: 0, pending: 0 });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load the student's institution assignments once they're signed in. The API
  // client caches this, so it's cheap across page loads; failures/empty results
  // simply leave the "Assigned" entry hidden.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setAssignmentInfo({ total: 0, pending: 0 });
      return undefined;
    }
    (async () => {
      try {
        const data = await listMyInstitutionAssignments();
        if (cancelled) return;
        const list = Array.isArray(data?.assignments) ? data.assignments : [];
        const pending = list.filter((a) => a.status === 'assigned' || a.status === 'started').length;
        setAssignmentInfo({ total: list.length, pending });
      } catch (_) {
        if (!cancelled) setAssignmentInfo({ total: 0, pending: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);



  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      navigate('/login');
    }
  };

  const isActivePath = (path) => {
    if (path === '/dashboard') return location?.pathname === path;
    if (path === '/interview-setup') {
      return ['/interview-setup', '/live-interview-panel', '/interview-report']?.includes(location?.pathname);
    }
    if (path === '/learn') {
      return location?.pathname === '/learn' || location?.pathname?.startsWith('/learn/');
    }
    if (path === '/community') {
      return location?.pathname === '/community' || location?.pathname?.startsWith('/community/');
    }
    if (path === '/blogs') {
      return location?.pathname?.startsWith('/blogs') || location?.pathname === '/blog-submit';
    }
    if (path === '/jobs') {
      return location?.pathname?.startsWith('/jobs');
    }
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navLinks = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Leaderboard', path: '/leaderboard', feature: 'leaderboard' },
    { label: 'Practice', path: '/interview-setup' },
    { label: 'ATS', path: '/resume-analyzer' },
    ...(assignmentInfo.total > 0
      ? [{ label: 'Assigned', path: '/assigned-interviews', count: assignmentInfo.pending }]
      : []),
    { label: 'Progress', path: '/profile-progress' },
    { label: 'Tools', isToolsDropdown: true },
    { label: 'Learn', path: '/learn', isMegaMenu: true, feature: 'learn' },
    { label: 'Community', path: '/community', feature: 'community' },
    { label: 'Blog', path: '/blogs', feature: 'blog' },
    { label: 'Jobs', path: '/jobs', feature: 'jobs' },
  ].filter(link => !link.feature || isEnabled(link.feature));

  const isLandingPage = location.pathname === '/' || location.pathname === '/landing-page';
  const headerClasses = isScrolled
    ? 'bg-white/85 dark:bg-[#08080a]/85 backdrop-blur-md shadow-sm dark:shadow-none border-b border-gray-100 dark:border-border'
    : 'bg-transparent dark:bg-transparent backdrop-blur-none border-b border-transparent';

  return (
    <>
      <header className={`fixed left-0 right-0 h-12 z-[100] transition-all duration-300 ${discountCampaign ? 'top-7' : 'top-0'} ${headerClasses}`}>
        <div className="h-full max-w-[1600px] mx-auto px-6 flex items-center relative">
          {/* Left: Logo */}
          <Link to="/" className="hover:opacity-80 transition-all relative z-10 shrink-0">
            <span className="text-xl font-normal tracking-tight text-slate-800 dark:text-slate-200">
              prepx<span className="text-emerald-500 dark:text-primary">ai</span>
            </span>
          </Link>

          {/* Center: Nav Links — centered on full header width */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((item) => {
              if (item.isMegaMenu) {
                return <LearnMegaMenu key={item.label} hideIcon triggerClassName={`flex items-center gap-1 px-3 py-3 text-xs font-medium transition-all border-b-2 ${isActivePath('/learn') ? 'border-emerald-400 dark:border-primary text-slate-800 dark:text-primary' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`} />;
              }
              if (item.isToolsDropdown) {
                return <ToolsDropdown key={item.label} isActive={location?.pathname === '/resume-analyzer' || location?.pathname === '/sandbox'} />;
              }
              const active = isActivePath(item.path);
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`px-3 py-3 text-xs font-medium transition-all border-b-2 ${active
                    ? 'border-emerald-400 dark:border-primary text-slate-800 dark:text-primary'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                    }`}
                >
                  <span className="inline-flex items-center">
                    {item.label}
                    {item.badge === 'free' && <FreeBadge />}
                    {item.count > 0 && <CountBadge count={item.count} />}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 ml-auto relative z-10 shrink-0">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex w-7 h-7 items-center justify-center rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-muted transition-all"
              aria-label="Toggle Theme"
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={15} />
            </button>

            {/* Pilot disabled: purchase/signup CTA kept for post-pilot restore.
            <button
              onClick={() => user ? navigate('/checkout') : openAuthModal('register')}
              className="hidden lg:flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 text-white rounded-lg text-[11px] font-extrabold tracking-wider shadow-lg shadow-emerald-600/20 dark:shadow-none transition-all hover:scale-[1.02] active:scale-[0.98] mx-1"
            >
              <Icon name="Zap" size={13} className="fill-white dark:fill-primary-foreground" />
              {user ? 'Upgrade to Pro' : 'Start Free Trial'}
            </button>
            */}

            <Link
              to="/checkout"
              aria-label="View pricing and buy coins"
              title="View pricing and buy coins"
              className="hidden md:flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-primary/10 border border-emerald-100 dark:border-primary/20 rounded-lg cursor-pointer hover:bg-emerald-100 dark:hover:bg-primary/20 transition-all group shrink-0"
            >
              <div className="w-5 h-5 rounded-full bg-emerald-500 dark:bg-primary flex items-center justify-center shadow-sm">
                <Icon name="Zap" size={12} color="#FFFFFF" className="fill-white" />
              </div>
              <span className="text-[12px] font-bold text-emerald-700 dark:text-primary">
                {user?.coins_balance || 0}
              </span>
            </Link>

            {/* User Avatar */}
            <div
              className="hidden md:flex items-center relative h-full"
              onMouseEnter={() => {
                if (window.profileTimeout) clearTimeout(window.profileTimeout);
                setIsAccountOpen(true);
              }}
              onMouseLeave={() => {
                window.profileTimeout = setTimeout(() => setIsAccountOpen(false), 200);
              }}
            >
              <button
                className="flex items-center justify-center w-7 h-7 rounded-full overflow-hidden border-2 border-emerald-400 dark:border-primary hover:border-emerald-500 dark:hover:border-primary transition-all"
                onClick={() => setIsAccountOpen(!isAccountOpen)}
              >
                {user?.profile_picture_url ? (
                  <img
                    src={user.profile_picture_url}
                    alt={user.full_name || 'User'}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-full h-full bg-emerald-100 dark:bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={16} className="text-emerald-600 dark:text-primary" />
                  </div>
                )}
              </button>

              {isAccountOpen && (
                <div className="absolute top-[100%] right-0 w-56 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-xl shadow-lg dark:shadow-none py-1.5 animate-slide-up z-50">
                  <div className="px-4 py-2.5 border-b border-slate-100 dark:border-border">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{user?.full_name || 'Student'}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{user?.email}</p>
                  </div>
                  <div className="py-1">
                    {/* <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2 text-xs text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-primary/10 hover:text-emerald-700 dark:hover:text-primary transition-all font-bold"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <Icon name="Settings" size={14} className="text-slate-400 dark:text-slate-500 group-hover:text-emerald-500 dark:group-hover:text-primary" />
                      Account Settings
                    </Link> */}
                    <Link
                      to="/profile-progress"
                      className="flex items-center gap-3 px-4 py-2 text-xs text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-primary/10 hover:text-emerald-700 dark:hover:text-primary transition-all font-bold"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <Icon name="TrendingUp" size={14} className="text-slate-400 dark:text-slate-500 group-hover:text-emerald-500 dark:group-hover:text-primary" />
                      My Progress
                    </Link>
                    {/* <Link
                      to="/support"
                      className="flex items-center gap-3 px-4 py-2 text-xs text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-primary/10 hover:text-emerald-700 dark:hover:text-primary transition-all font-bold"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <Icon name="HelpCircle" size={14} className="text-slate-400 dark:text-slate-500 group-hover:text-emerald-500 dark:group-hover:text-primary" />
                      Support / Contact
                    </Link> */}
                  </div>
                  <div className="pt-1 border-t border-slate-100 dark:border-border">
                    <button
                      className="flex items-center gap-3 px-4 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all w-full text-left font-bold"
                      onClick={handleLogout}
                    >
                      <Icon name="LogOut" size={14} />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-muted text-slate-500 dark:text-slate-400 transition-all"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[200] md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={toggleMobileMenu}
          />
          <div className="absolute top-12 left-0 right-0 bg-white dark:bg-card border-b border-gray-200 dark:border-border shadow-lg dark:shadow-none animate-slide-in">
            <nav className="p-4 space-y-1">
              {navLinks.filter(l => !l.isMegaMenu && !l.isToolsDropdown).map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={toggleMobileMenu}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActivePath(item.path)
                    ? 'text-emerald-600 bg-emerald-50 dark:text-primary dark:bg-primary/10'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-muted'
                    }`}
                >
                  <span className="inline-flex items-center">
                    {item.label}
                    {item.badge === 'free' && <FreeBadge />}
                    {item.count > 0 && <CountBadge count={item.count} />}
                  </span>
                </Link>
              ))}
              <Link
                to="/learn"
                onClick={toggleMobileMenu}
                className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActivePath('/learn')
                  ? 'text-emerald-600 bg-emerald-50 dark:text-primary dark:bg-primary/10'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-muted'
                  }`}
              >
                Learn
              </Link>

              <div className="pt-2 pb-2">
                <p className="px-4 py-1 text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-widest">Tools</p>
                <Link to="/resume-analyzer" onClick={toggleMobileMenu} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-muted transition-all">
                  <Icon name="FileText" size={16} /> Resume Analyzer
                </Link>
                <Link to="/sandbox" onClick={toggleMobileMenu} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-muted transition-all">
                  <Icon name="TerminalSquare" size={16} /> Sandbox IDE
                </Link>
              </div>

              <div className="pt-4 border-t border-gray-100 dark:border-border space-y-1">
                <div className="px-4 py-3 mb-2 bg-gray-50 dark:bg-muted rounded-lg">
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{user?.full_name || 'Student'}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                  <Link
                    to="/checkout"
                    onClick={toggleMobileMenu}
                    className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-border hover:text-emerald-600 dark:hover:text-primary transition-colors"
                    aria-label="View pricing and buy coins"
                  >
                    <Icon name="Zap" size={14} className="text-yellow-500 fill-yellow-500" />
                    <span className="text-xs font-bold text-yellow-600 dark:text-yellow-500">
                      {user?.coins_balance || 0} Coins
                    </span>
                  </Link>
                </div>
                <Link
                  to="/settings"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-muted transition-all w-full"
                  onClick={toggleMobileMenu}
                >
                  <Icon name="Settings" size={18} />
                  Settings
                </Link>
                <button
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all w-full text-left"
                  onClick={handleLogout}
                >
                  <Icon name="LogOut" size={18} />
                  Sign Out
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default NavigationHeader;
